import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { videoService, type VideoWithProvider } from '../../services'
import { VideoGrid } from '../../components/video'
import { Button } from '../../components/common'

// Hero slides data
const HERO_SLIDES = [
  {
    id: 1,
    title: 'Your Personal Training,',
    subtitle: 'Set Up Just For You',
    description: 'Get customized exercise programs designed by certified professionals, tailored to your goals and delivered through easy-to-follow video guides.',
    gradient: 'from-rose-600 via-red-600 to-orange-500',
    bgImage: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=2000&q=80',
    cta: { text: 'Start Training', link: '/register' },
    secondaryCta: { text: 'Browse Programs', link: '/videos' },
  },
  {
    id: 2,
    title: 'Find Expert Coaches',
    subtitle: '& Training Videos',
    description: 'Search our database of certified coaches sharing proven training methods and excellent instructional videos.',
    gradient: 'from-emerald-600 via-teal-600 to-cyan-500',
    bgImage: 'https://images.unsplash.com/photo-1576678927484-cc907957088c?auto=format&fit=crop&w=2000&q=80',
    cta: { text: 'Find a Coach', link: '/videos' },
    secondaryCta: { text: 'Learn More', link: '/register' },
  },
  {
    id: 3,
    title: 'Exercise Programs,',
    subtitle: 'Delivered via Video',
    description: 'The all-in-one platform for physical therapists, coaches, and trainers to create, share, and track personalized exercise programs with their clients.',
    gradient: 'from-blue-600 via-indigo-600 to-purple-600',
    bgImage: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=2000&q=80',
    cta: { text: 'Get Started Free', link: '/register' },
    secondaryCta: { text: 'Browse Videos', link: '/videos' },
  },
]

export function HomePage() {
  const [featuredVideos, setFeaturedVideos] = useState<VideoWithProvider[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length)
        setIsTransitioning(false)
      }, 300)
    }, 10000)

    return () => clearInterval(timer)
  }, [])

  const goToSlide = useCallback((index: number) => {
    if (index === currentSlide) return
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentSlide(index)
      setIsTransitioning(false)
    }, 300)
  }, [currentSlide])

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        const videos = await videoService.getFeaturedVideos(6)
        setFeaturedVideos(videos)
      } catch (error) {
        console.error('Failed to load featured videos:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadFeatured()
  }, [])

  const slide = HERO_SLIDES[currentSlide]

  return (
    <div>
      {/* Hero Section with Slideshow */}
      <section className="relative min-h-[600px] md:min-h-[700px] overflow-hidden">
        {/* Background Image with Overlay */}
        <div
          className={`absolute inset-0 transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
          style={{
            backgroundImage: `url(${slide.bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Gradient Overlay */}
          <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradient} opacity-85`} />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/30" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className={`max-w-3xl transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white drop-shadow-lg">
              {slide.title}
              <br />
              <span className="text-white/95">{slide.subtitle}</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/90 max-w-2xl drop-shadow">
              {slide.description}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={slide.cta.link}>
                <Button size="lg" variant="secondary" className="shadow-xl hover:shadow-2xl transition-shadow">
                  {slide.cta.text}
                </Button>
              </Link>
              <Link to={slide.secondaryCta.link}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/20 backdrop-blur-sm"
                >
                  {slide.secondaryCta.text}
                </Button>
              </Link>
            </div>
          </div>

          {/* Slide Indicators */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3">
            {HERO_SLIDES.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentSlide
                    ? 'w-8 h-3 bg-white'
                    : 'w-3 h-3 bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">
              Everything You Need to Deliver Better Training
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              From video creation to client progress tracking, ExerciseToolbox
              gives you the tools to grow your practice.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Easy Video Upload
              </h3>
              <p className="text-gray-600">
                Upload exercise videos up to 3 minutes long. Automatic
                transcoding and adaptive streaming included.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Build Exercise Programs
              </h3>
              <p className="text-gray-600">
                Create custom exercise playlists for your clients. Add notes,
                sets, and reps to each exercise.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Track Progress
              </h3>
              <p className="text-gray-600">
                See which exercises your clients complete. Get notified when
                they need extra support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Videos Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Featured Videos
              </h2>
              <p className="mt-1 text-gray-600">
                Browse exercises from top providers
              </p>
            </div>
            <Link
              to="/videos"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              View all &rarr;
            </Link>
          </div>

          <VideoGrid
            videos={featuredVideos}
            isLoading={isLoading}
            emptyMessage="No featured videos yet. Check back soon!"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold">
            Ready to Transform Your Practice?
          </h2>
          <p className="mt-4 text-lg text-blue-100">
            Join thousands of providers who use ExerciseToolbox to deliver
            better care to their clients.
          </p>
          <div className="mt-8">
            <Link to="/register">
              <Button size="lg" variant="secondary">
                Start Your Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
